import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    LinearProgress,
    Paper
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import { exportRowsToCsv } from '../../utils/csvExport';
import { formatInteger } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    buildInadimplentesRows,
    fetchListaInadimplentes
} from './inadimplenciaData';
import '../css/biAluguelAccess.css';
import '../css/biInadimplencia.css';

const emptyRowsLabel = 'Nenhum inadimplente encontrado para o período selecionado.';
const csvBaseName = 'lista-inadimplentes';

const ListaInadimplentesBIGE = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dataInicio = searchParams.get('data_inicio');
    const dataFim = searchParams.get('data_fim');
    const [inadimplentes, setInadimplentes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadInadimplentes = useCallback(async () => {
        setIsLoading(true);
        const listaInadimplentes = await fetchListaInadimplentes({
            dataInicio,
            dataFim
        });
        setInadimplentes(listaInadimplentes);
        setIsLoading(false);
    }, [dataFim, dataInicio]);

    useEffect(() => {
        loadInadimplentes();
    }, [loadInadimplentes]);

    const rows = useMemo(() => buildInadimplentesRows(inadimplentes), [inadimplentes]);

    const columns = useMemo(() => [
        {
            field: 'clienteId',
            headerName: 'Cliente ID',
            type: 'number',
            width: 140,
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: 'razaoSocial',
            headerName: 'Razão social',
            minWidth: 320,
            flex: 1.5
        },
        {
            field: 'contatoNome',
            headerName: 'Contato',
            minWidth: 180,
            flex: 0.7
        },
        {
            field: 'contatoTelefone',
            headerName: 'Telefone',
            minWidth: 160,
            flex: 0.5
        },
        {
            field: 'dividaTotal',
            headerName: 'Dívida total',
            type: 'number',
            minWidth: 190,
            flex: 0.6,
            valueFormatter: (value) => formatCurrency(value)
        },
        {
            field: 'quantidade',
            headerName: 'Quantidade',
            type: 'number',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatInteger(value)
        }
    ], []);

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-inadimplencia-page'>
            <PageHeader>{`Lista inadimplentes ${dataInicio} a ${dataFim}`}</PageHeader>

            <PageIntro eyebrow='BI Cobrança' title='Lista de inadimplentes'>
                Clientes inadimplentes no intervalo selecionado, com faturas e valores em aberto.
            </PageIntro>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-inadimplencia-list-topbar'>
                    <Button
                        className='bi-ge-inadimplencia-back-button'
                        variant='outlined'
                        startIcon={<ArrowBackRoundedIcon />}
                        onClick={() => navigate(-1)}
                    >
                        Voltar
                    </Button>
                </div>

                <Paper className='bi-aluguel-access-panel bi-aluguel-access-table-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Inadimplentes filtrados</p>
                            <h2>Lista por cliente</h2>
                        </div>

                        <div className='bi-ge-inadimplencia-panel-actions'>
                            <Button
                                className='bi-ge-inadimplencia-export-button'
                                variant='outlined'
                                startIcon={<DownloadRoundedIcon />}
                                disabled={isLoading || rows.length === 0}
                                onClick={() => exportRowsToCsv({ baseName: csvBaseName, columns, rows })}
                            >
                                Exportar CSV
                            </Button>
                            <FormatListBulletedRoundedIcon />
                        </div>
                    </div>

                    <div className='bi-aluguel-access-grid-shell bi-ge-inadimplencia-list-grid-shell'>
                        <DataGrid
                            className='bi-aluguel-access-grid'
                            rows={rows}
                            columns={columns}
                            loading={isLoading}
                            disableRowSelectionOnClick
                            localeText={{ noRowsLabel: emptyRowsLabel }}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 50
                                    }
                                },
                                sorting: {
                                    sortModel: [{ field: 'dividaTotal', sort: 'desc' }]
                                }
                            }}
                        />
                    </div>
                </Paper>
            </section>
        </Box>
    );
};

export default ListaInadimplentesBIGE;
